import { PlatformEnum } from '@vacationtracker/shared/data/platforms'

export interface IRememberedLogin {
  email: string
  platform: PlatformEnum
  savedAt: string
  /**
   * Both come from the lookup and belong to a password account. The username is what lets a
   * return visit open straight on the password step, since signing in cannot be attempted
   * without it.
   */
  username?: string
  adpEnabled?: boolean
}

export type RememberedLoginInput = Omit<IRememberedLogin, 'savedAt'>

const STORAGE_KEY = 'vtLastLogin'

const VALID_PLATFORMS: PlatformEnum[] = [PlatformEnum.Slack, PlatformEnum.Microsoft, PlatformEnum.Google, PlatformEnum.Email]

const isValidPlatform = (value: unknown): value is PlatformEnum => typeof value === 'string' && VALID_PLATFORMS.includes(value as PlatformEnum)

export const getRememberedLogin = (): IRememberedLogin | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return null
    const parsed = JSON.parse(stored) as Partial<IRememberedLogin>
    if (typeof parsed?.email !== 'string' || !parsed.email) return null
    if (!isValidPlatform(parsed.platform)) return null
    return {
      email: parsed.email,
      platform: parsed.platform,
      savedAt: typeof parsed.savedAt === 'string' ? parsed.savedAt : '',
      username: typeof parsed.username === 'string' && parsed.username ? parsed.username : undefined,
      adpEnabled: parsed.adpEnabled === true,
    }
  } catch {
    return null
  }
}

export const saveRememberedLogin = (login: RememberedLoginInput): void => {
  try {
    if (!login.email || !isValidPlatform(login.platform)) return
    // Signing in reports the address and platform only, and runs after the lookup that knew
    // the rest, so a later write for the same account must not drop what the lookup stored.
    const stored = getRememberedLogin()
    const carried = stored?.email === login.email && stored.platform === login.platform ? stored : undefined
    const toStore: IRememberedLogin = {
      email: login.email,
      platform: login.platform,
      username: login.username ?? carried?.username,
      adpEnabled: login.adpEnabled ?? carried?.adpEnabled,
      savedAt: new Date().toISOString(),
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore))
  } catch {
    // no-op
  }
}

export const clearRememberedLogin = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // no-op
  }
}

const PROVIDER_ACCOUNTS_KEY = 'vtLastProviderAccounts'

const readProviderAccounts = (): Record<string, string> => {
  try {
    const parsed = JSON.parse(localStorage.getItem(PROVIDER_ACCOUNTS_KEY) || '{}')
    return typeof parsed === 'object' && parsed !== null ? parsed : {}
  } catch {
    return {}
  }
}

/**
 * The address each provider last signed someone in as, in this browser. Unlike the marker above
 * it is written only once the provider has answered (whether or not Vacation Tracker then let the
 * account in), never by a lookup, so typing another address does not overwrite it. Signing out of
 * Vacation Tracker leaves the provider signed in, so this is who a new sign-in with that provider
 * will most likely come back as.
 */
export const rememberProviderAccount = (account: { platform: string; email: string }): void => {
  if (!account.email) return
  try {
    localStorage.setItem(PROVIDER_ACCOUNTS_KEY, JSON.stringify({ ...readProviderAccounts(), [account.platform]: account.email.toLowerCase() }))
  } catch {
    // no-op
  }
}

export const getProviderAccount = (platform: string): string | undefined => readProviderAccounts()[platform]

/**
 * Persist the marker, and the provider accounts, across logout. Callers must read this BEFORE
 * localStorage.clear() and re-set the entries after, otherwise the marker is lost and a returning
 * visitor lands on the generic email step.
 */
export const collectRememberedLoginStorage = (): [string, string][] => {
  const entries: [string, string][] = []
  try {
    for (const key of [STORAGE_KEY, PROVIDER_ACCOUNTS_KEY]) {
      const stored = localStorage.getItem(key)
      if (stored) entries.push([key, stored])
    }
  } catch {
    // no-op
  }
  return entries
}
